import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useMutation, useQuery } from '@apollo/client';
import { EllipsisOutlined, QuestionCircleTwoTone } from '@ant-design/icons';
import { message, Popover, Tooltip } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { capitalize } from 'lodash';

import TitleBar from '../../components/TitleBar';
import { Button } from '../../components';
import AccessIcon from '../../components/assets/AccessIcon';
import AntModal from '../../components/generic/AntModal';
import TableV3, {
  ModifyVariablesArgs,
} from '../../components/generic/Table/TableV3';

import {
  CREATE_USER,
  DELETE_USER,
  GET_ALL_USERS,
  UPDATE_USER,
  CREATE_ROLES,
} from './queries';
import { FETCH_DIVISIONS } from '../dataInputs/queries';
import { FIND_ALL_LOCATIONS } from '../bcp/queries';
import { createUserFormFields } from './forms/createFormFields';
import { editUserFormFields } from './forms/editFormFields';
import { PermissionsToggles, User, Users } from './types';
import { DEFAULT_GLOBAL_PERMISSIONS, USER_TYPES } from './constants';
import { baseColumns } from './columns';
import { getOrganization } from '../../utils/config';
import { tableSortingMethods } from '../../utils';

import UserTypesTable from './UserTypesTable';
import PermissionTogglesTable from './PermissionTogglesTable';
import QueryErrorBoundary from '../../components/error-handling';
import { useUserContext } from '../../context/PermissionsContext';
import { useAuth0 } from '@auth0/auth0-react';
import {
  GlobalPermissions,
  MultiSelect,
  UserLevelPermissions,
} from './components';
import TitleUpdater from '../../components/common/TitleUpdater';

const UsersManagement = () => {
  const USERS_QUERY_ID = 'GetAllUsers';
  const USER_PERMISSIONS_QUERY_ID = 'GetUserPermissions';

  const uuid = getOrganization();
  const navigate = useNavigate();

  const [grantAccessModalOpen, setGrantAccessModalOpen] = useState(false);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [shouldTriggerSubmit, setShouldTriggerSubmit] = useState(false);
  const [userTypeModal, setUserTypeModal] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [userTypes, setUserTypes] = useState<any[] | null>(null);
  const [selectedTags, setSelectedTags] = useState<string[] | null>(null);
  const [currentUserPermissionsId, setCurrentUserPermissionsId] = useState<
    string | null
  >(null);
  const [isGlobalMode, setIsGlobalMode] = useState(false);
  const [globalSelectedValue, setGlobalSelectedValue] = useState<string[]>([]);
  const [globalPermissions, setGlobalPermissions] =
    useState<PermissionsToggles>(DEFAULT_GLOBAL_PERMISSIONS);

  const { user: auth0User } = useAuth0();
  const { isAdmin } = useUserContext();

  const {
    data: users,
    loading: usersLoading,
    error: usersError,
  } = useQuery<Users>(GET_ALL_USERS, {
    variables: { uuid },
    skip: !uuid,
  });
  const usersData = users?.findAll || [];

  const { data: divisions } = useQuery(FETCH_DIVISIONS, {});

  const selectedUsers = usersData.filter((user) =>
    selectedRowKeys.includes(user.uuid),
  );

  const divisionsData = divisions?.findAllDivisions || [];

  const { data: locations } = useQuery(FIND_ALL_LOCATIONS, {});
  const locationsData = locations?.findAllLocations || [];

  const [createRole, { loading: assignPermissionsLoading }] = useMutation(
    CREATE_ROLES,
    {
      refetchQueries: [USERS_QUERY_ID, USER_PERMISSIONS_QUERY_ID],
    },
  );

  const [createGQL] = useMutation(CREATE_USER, {
    refetchQueries: [USERS_QUERY_ID],
  });
  const [updateGQL] = useMutation(UPDATE_USER, {
    refetchQueries: [USERS_QUERY_ID],
  });
  const [deleteGQL] = useMutation(DELETE_USER, {
    refetchQueries: [USERS_QUERY_ID],
  });

  const userType = userTypes?.find(
    (item) => item.uuid === currentUserPermissionsId,
  )?.userType;

  const handleAssignPermissions = async (data: any) => {
    try {
      await createRole({
        variables: {
          data,
        },
      });
      message.success('Permissions assigned successfully');
    } catch (error) {
      console.log('error:', error);
      message.error('Failed to assign permissions');
    }
  };

  const handleOpenPermissionsModal = (recordId: string) => {
    setCurrentUserPermissionsId(recordId);
    setUserTypeModal(true);
  };

  const handleGrantAccess = async () => {
    if (!currentUserId) return;
    setShouldTriggerSubmit(true);
    setGrantAccessModalOpen(false);
  };

  const handlePermissionsButtonClick = (recordId: any) => {
    setCurrentUserId(recordId);
    setGrantAccessModalOpen(true);
  };

  const handleSelectedValueChange = (value: any, uuid: string) => {
    if (!Object.values(USER_TYPES).includes(value[0])) return;

    setUserTypes((prev: any) =>
      prev.map((item: any) =>
        item.uuid === uuid ? { ...item, userType: value[0] } : item,
      ),
    );

    if (value[0] === USER_TYPES.DIVISION || value[0] === USER_TYPES.LOCATION) {
      setCurrentUserPermissionsId(uuid);
      setUserTypeModal(true);
      setSelectedTags(null);
      return;
    }

    if (value[0] === USER_TYPES.CORPORATE) {
      handleAssignPermissions({
        userAuth0ID: uuid,
        selectedValue: USER_TYPES.CORPORATE.toUpperCase(),
        generalPermissions: usersData.find((user) => user.uuid === uuid)?.roles
          .generalPermissions,
      });
    }
  };

  const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const handleAssignSelectedTags = () => {
    if (!selectedTags?.length) {
      message.error('Please select at least one item');
      return;
    }

    if (isGlobalMode) {
      setUserTypeModal(false);
      return;
    }

    handleAssignPermissions({
      userAuth0ID: currentUserPermissionsId,
      selectedValue: userType?.toUpperCase(),
      selectedTags,
      generalPermissions: {
        ...currentUser?.roles.generalPermissions,
        admin: false,
      },
    });
    setUserTypeModal(false);
    setSelectedTags(null);
    setCurrentUserPermissionsId(null);
  };

  const currentUser = usersData.find(
    (user) => user.uuid === currentUserPermissionsId,
  );

  const IS_DIVISION =
    userTypes?.find((item) => item.uuid === currentUserPermissionsId)
      ?.userType === USER_TYPES.DIVISION ||
    globalSelectedValue[0] === USER_TYPES.DIVISION;

  const shouldShouldTags =
    currentUser?.roles.selectedValue ===
    userTypes
      ?.find((item) => item.uuid === currentUserPermissionsId)
      ?.userType?.toUpperCase();

  const formattedLocations = locationsData?.map((location: any) => ({
    ...location,
    name: location.locationName,
  }));

  const handleCloseTagsModal = () => {
    if (!userTypes?.length) return;

    const wereChangesCommited =
      currentUser?.roles.selectedTags.length !== selectedTags?.length;

    if (wereChangesCommited) {
      const previousRole = currentUser?.roles.selectedValue;

      const restoredTypes = userTypes.map((user) => {
        if (user.uuid === currentUserPermissionsId) {
          return { ...user, userType: capitalize(previousRole) };
        }

        return user;
      });
      setUserTypes(restoredTypes);
    }

    setUserTypeModal(false);
    setCurrentUserPermissionsId(null);

    if (!isGlobalMode) {
      setSelectedTags(null);
    }
  };

  const extendedActions = (record: User) => {
    const CONNECTION_TYPE = 'Username-Password-Authentication';
    const SHOULD_SHOW_ACCESS_BUTTON =
      !record?.connection?.includes(CONNECTION_TYPE);

    return (
      <div className="flex justify-end items-center gap-2">
        {SHOULD_SHOW_ACCESS_BUTTON && (
          <Button
            type={'edit'}
            onClick={() => handlePermissionsButtonClick(record.uuid)}
          >
            <Tooltip title="Grant SSO Access">
              <div>
                <AccessIcon style={{ fill: '#437EF7' }} />
              </div>
            </Tooltip>
          </Button>
        )}
        <Button
          type={'edit'}
          onClick={() =>
            navigate(`/users-management/${record.uuid}?name=${record.name}`)
          }
        >
          <Tooltip title="User Details">
            <EllipsisOutlined className=" text-blue hover:text-white" />
          </Tooltip>
        </Button>
      </div>
    );
  };

  const modifyVariables = ({ values, type }: ModifyVariablesArgs) => {
    if (type === 'create') {
      return {
        name: values.name,
        email: values.email,
        picture: values.picture,
        connectionType: true,
        organizationUuid: uuid,
      };
    }
    return {
      ...values,
    };
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  const columns: ColumnsType<User> = [
    {
      key: 'picture',
      title: '',
      align: 'center',
      width: 100,
      render: ({ picture }) => (
        <div className="flex justify-center items-center">
          <img src={picture} alt="user" className="w-8 h-8 rounded-full" />
        </div>
      ),
    },
    {
      title: 'Name',
      key: 'name',
      align: 'left',
      width: 150,
      sorter: (a, b) => tableSortingMethods.string(a.name, b.name),
      render: ({ name }) => (
        <span className="custom-table--column text-sm">{name}</span>
      ),
    },
    {
      key: 'userType',
      title: () => (
        <div className="flex items-center justify-center gap-2">
          User Type
          <Popover
            placement="bottom"
            content={UserTypesTable}
            overlayInnerStyle={{ width: '550px' }}
          >
            <QuestionCircleTwoTone />
          </Popover>
        </div>
      ),
      align: 'center',
      width: 80,
      render: ({ uuid }) => {
        const userPermissions = userTypes?.find((item) => item.uuid === uuid);

        const SHOULD_DISPLAY_ICON_LIST =
          userPermissions?.userType === USER_TYPES.DIVISION ||
          userPermissions?.userType === USER_TYPES.LOCATION;

        return (
          <UserLevelPermissions
            handleAssignPermissions={handleAssignPermissions}
          >
            <UserLevelPermissions.TagsSelect
              userId={uuid}
              userPermissions={userPermissions}
              displayIconList={SHOULD_DISPLAY_ICON_LIST}
              selectedRowKeys={selectedRowKeys}
              handleSelectedValueChange={handleSelectedValueChange}
              handleOpenPermissionsModal={handleOpenPermissionsModal}
            />
          </UserLevelPermissions>
        );
      },
    },
    {
      key: 'permissions',
      title: () => (
        <div className="flex items-center justify-center gap-2">
          Permisssions
          <Popover
            placement="bottom"
            content={PermissionTogglesTable}
            overlayInnerStyle={{ width: '550px' }}
          >
            <QuestionCircleTwoTone />
          </Popover>
        </div>
      ),
      align: 'center',
      width: 80,
      render: ({ uuid, roles: { generalPermissions, selectedValue } }) => {
        const disabled = selectedRowKeys.length > 1;
        const user = usersData.find((user) => user.uuid === uuid);
        const roles = user ? user.roles : {};
        const { __typename, ...rest } = roles as {
          __typename: string;
          [key: string]: unknown;
        };

        const {
          active,
          admin,
          export: exp,
          financials,
          edit,
          approver,
        } = generalPermissions;

        const reorderedObject = {
          active,
          admin,
          export: exp,
          financials,
          edit,
          approver,
        };

        return (
          <div className="w-full h-full flex items-center gap-1">
            {Object.entries(reorderedObject).map(([key, value]: any) => {
              const isCurrentUser = uuid === auth0User?.sub;
              return (
                <UserLevelPermissions
                  key={key}
                  handleAssignPermissions={handleAssignPermissions}
                >
                  <UserLevelPermissions.TogglePermissions
                    permissionKey={key}
                    uuid={uuid}
                    value={value}
                    disabled={disabled}
                    isCurrentUser={isCurrentUser}
                    generalPermissions={generalPermissions}
                    rest={rest}
                    selectedValue={selectedValue}
                  />
                </UserLevelPermissions>
              );
            })}
          </div>
        );
      },
    },
    {
      key: 'email',
      title: 'Email',
      align: 'left',
      width: 100,
      sorter: (a, b) => tableSortingMethods.string(a.email, b.email),
      render: ({ email }) => (
        <span className="custom-table--column text-sm">{email}</span>
      ),
    },
  ];

  useEffect(() => {
    if (selectedRowKeys.length > 1) {
      setIsGlobalMode(true);
    } else {
      setIsGlobalMode(false);
    }
  }, [selectedRowKeys]);

  useEffect(() => {
    if (usersData?.length) {
      setUserTypes(
        usersData.map((user) => ({
          uuid: user.uuid,
          userType: capitalize(user?.roles?.selectedValue?.toLowerCase()),
        })),
      );
    }
  }, [selectedRowKeys, usersData]);

  return (
    <>
      <TitleUpdater title="Users Management" />
      <TitleBar title="Users Management" />
      <QueryErrorBoundary error={usersError}>
        <TableV3
          title="Users"
          columns={columns}
          data={usersData}
          loading={usersLoading || assignPermissionsLoading}
          editable
          formFields={createUserFormFields}
          editFormFields={editUserFormFields}
          createGQL={createGQL}
          updateGQL={updateGQL}
          deleteGQL={deleteGQL}
          extendActions={extendedActions}
          modifyVariables={modifyVariables}
          forceSubmit={shouldTriggerSubmit}
          forceSubmitRecordIndex={currentUserId}
          exportExcelColumns={baseColumns}
          rowSelection={{
            type: 'checkbox',
            ...rowSelection,
          }}
          selectedRows={selectedRowKeys}
          multiSelectComponent={
            <GlobalPermissions
              usersData={usersData}
              selectedRowKeys={selectedRowKeys}
              setSelectedRowKeys={setSelectedRowKeys}
              globalSelectedValue={globalSelectedValue}
              setGlobalSelectedValue={setGlobalSelectedValue}
              globalPermissions={globalPermissions}
              setGlobalPermissions={setGlobalPermissions as any}
              selectedUsers={selectedUsers}
              selectedTags={selectedTags}
              setSelectedTags={setSelectedTags}
              setUserTypeModal={setUserTypeModal}
              auth0User={auth0User}
              isAdmin={isAdmin}
              handleAssignPermissions={handleAssignPermissions}
            />
          }
          customStyles="custom-column-PA--heading"
        />
      </QueryErrorBoundary>
      <AntModal
        title={'Grant Access'}
        openModal={grantAccessModalOpen}
        onCancel={() => setGrantAccessModalOpen(false)}
      >
        <div className="w-full flex flex-col justify-between">
          <p className="pb-6">
            Are you sure you want to grant access to this user?
          </p>
          <div className="flex justify-end items-center gap-4">
            <Button type="default" onClick={handleGrantAccess}>
              Confirm
            </Button>
            <Button
              type="warning"
              onClick={() => setGrantAccessModalOpen(false)}
            >
              Cancel
            </Button>
          </div>
        </div>
      </AntModal>
      <AntModal
        title={`${
          currentUser?.name.split(' ')[0].toUpperCase() || 'GLOBAL PERMISSIONS'
        } - SELECT ${IS_DIVISION ? 'DIVISIONS' : 'LOCATIONS'}`}
        openModal={userTypeModal}
        onCancel={handleCloseTagsModal}
        width={500}
      >
        <div className="flex flex-col gap-4">
          <div>
            Here you can manage fine grained permissions for the user. Please
            select the {IS_DIVISION ? 'divisions' : 'locations'} you want to let
            the user access:
          </div>
          <MultiSelect
            rolesData={IS_DIVISION ? divisionsData : formattedLocations}
            type={IS_DIVISION ? USER_TYPES.DIVISION : USER_TYPES.LOCATION}
            selectedTags={
              !selectedTags && shouldShouldTags
                ? currentUser?.roles.selectedTags || []
                : selectedTags || []
            }
            setSelectedTags={setSelectedTags}
          />
          <Button
            type="primary"
            onClick={handleAssignSelectedTags}
            className="min-w-[180px] mx-auto"
          >
            Save
          </Button>
        </div>
      </AntModal>
    </>
  );
};

export default UsersManagement;
